import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Dayjs } from "dayjs"
import styled from "styled-components"

import { useLazyGetMessageTemplatesQuery } from "store/api/contractorsApi"
import { UITypes } from "types"

import { Spinner } from "components/common/Spinner"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import { SelectDynamic } from "components/form/SelectDynamic"
import { SelectStatic } from "components/form/SelectStatic"

import MessageScheduleDateTimePicker from "./MessageScheduleDateTimePicker"

import styles from "./index.module.scss"

const ScheduleForLaterButtonContainer = styled.div<{ clicked: boolean }>`
  margin-left: 14px;
  position: relative;

  border: 2px solid ${({ clicked }) => (clicked ? "#eaeaea" : "transparent")};
  padding: 2px 2px;
  border-radius: 25px;

  @media only screen and (max-width: 820px) {
    position: static;
  }
`

interface MessageCenterHeaderProps {
  templateID: UITypes.Option[] | undefined
  setTemplateID: (value: UITypes.Option[] | undefined) => void
  deviceIDs: number[]
  onSendMessage: () => void
  onSendScheduledMessage: (dateTime: Dayjs) => void
  isGenerateMessagesFetching: boolean
  tableHeaderCells: UITypes.TableHeaderCell[]
  hasMessagesCenterAccess: boolean
}

export const MessageCenterHeader = ({
  templateID,
  setTemplateID,
  deviceIDs,
  onSendMessage,
  onSendScheduledMessage,
  isGenerateMessagesFetching,
  tableHeaderCells,
  hasMessagesCenterAccess,
}: MessageCenterHeaderProps) => {
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [selectedDateTime, setSelectedDateTime] = useState<Dayjs | null>(null)
  const navigate = useNavigate()

  const handleShowScheduleModal = () => {
    setShowScheduleModal(true)
  }

  const handleDateTimeChange = (dateTime: Dayjs) => {
    setSelectedDateTime(dateTime)
  }

  const handleCancelScheduleModal = () => {
    setShowScheduleModal(false)
    setSelectedDateTime(null)
  }

  const handleScheduleMessage = () => {
    if (!selectedDateTime) return

    setShowScheduleModal(false)
    onSendScheduledMessage(selectedDateTime)
  }

  return (
    <nav className={styles.tableNavWrapper}>
      <div className={styles.leftNavWrapper}>
        <ButtonNew onClick={() => navigate("/messages/create-template")} color="blue">
          Create Template
        </ButtonNew>
        {hasMessagesCenterAccess ? (
          <SelectDynamic
            className={styles.selectWrapper}
            valueName="id"
            labelName="title"
            useLazyQuery={useLazyGetMessageTemplatesQuery}
            values={templateID || []}
            onChange={setTemplateID}
          />
        ) : (
          <SelectStatic values={templateID || []} onChange={setTemplateID} options={templateID || []} />
        )}
        {templateID?.length && deviceIDs?.length ? (
          <>
            <ButtonNew className={styles.actionButton} onClick={onSendMessage} color={"blue"}>
              {isGenerateMessagesFetching ? <Spinner /> : "Send now"}
            </ButtonNew>
            <ScheduleForLaterButtonContainer className={styles.scheduleButtonContainer} clicked={showScheduleModal}>
              <ButtonNew color="white" onClick={handleShowScheduleModal}>
                Schedule for later
              </ButtonNew>
              {showScheduleModal && (
                <MessageScheduleDateTimePicker
                  onCancel={handleCancelScheduleModal}
                  onSubmit={handleScheduleMessage}
                  selectedDateTime={selectedDateTime}
                  onDateTimeChange={handleDateTimeChange}
                />
              )}
            </ScheduleForLaterButtonContainer>
          </>
        ) : (
          <>
            <ButtonNew className={styles.actionButton} disabled color="grey">
              Send now
            </ButtonNew>
          </>
        )}
      </div>
      <div className={styles.tableNavButtonsWrapper}>
        <EditColumnsButton tableName="contractor_messages_list" tableHeaderCells={tableHeaderCells} />
        <ButtonNew className={styles.tableNavItem} onClick={() => navigate("/messages/history")}>
          History
        </ButtonNew>
        {/* <ButtonNew className={styles.tableNavItem} disabled>Export</ButtonNew> */}
      </div>
    </nav>
  )
}
